import AuthorizedPage from "@/models/base/AuthorizedPage";
import React from "react";
import {
    Button,
    ButtonType, CellAction,
    CellModel,
    ColumnDefinition,
    ColumnType,
    Grid,
    GridHoveringType,
    GridModel,
    GridOddType,
    IconSize,
    Inline,
    JustifyContent,
    PageContainer,
    PageHeader,
    PageRow,
    RowModel,
    ToolbarContainer
} from "@renta-apps/athenaeum-react-components";
import Localizer from "@/localization/Localizer";
import Group from "@/models/server/Group";
import { ActionType, TextAlign } from "@renta-apps/athenaeum-react-common";
import RentaTaskConstants from "@/helpers/RentaTaskConstants";
import { getCellAction } from "@/helpers/GridHelpers";
import AddGroupRequest from "@/models/server/requests/AddGroupRequest";
import SaveGroupRequest from "@/models/server/requests/SaveGroupRequest";

import styles from "./Groups.module.scss";

interface IGroupProps {
}

interface IGroupState {
    groups: Group[],
}

export default class GroupManagement extends AuthorizedPage<IGroupProps, IGroupState> {
    state: IGroupState = {
        groups: [],
    }

    private readonly _gridRef: React.RefObject<Grid<Group>> = React.createRef();

    private readonly _columns: ColumnDefinition[] = [
        {
            header: "#",
            accessor: "#",
            className: "grey",
            textAlign: TextAlign.Center,
            minWidth: 40,
            noWrap: true
        },
        {
            header: Localizer.genericName,
            accessor: nameof<Group>(g => g.name),
            type: ColumnType.Text,
            minWidth: 600,
            editable: true,
            settings: {
                maxLength: RentaTaskConstants.dbKeyLength,
            },
            callback: async (cell: CellModel<Group>, _) => await this.onNameChangeAsync(cell)
        },
        {
            header: Localizer.userManagementUsersTab,
            accessor: nameof<Group>(g => g.userCount),
            type: ColumnType.Number,
            minWidth: 60,
            editable: false,
        },
        {
            header: Localizer.genericActions,
            init: (cell) => this.initActions(cell),
            minWidth: 70,
            maxWidth: 70,
            actions: [
                {
                    name: "save",
                    title: Localizer.genericActionSaveLanguageItemName,
                    icon: "far save",
                    type: ActionType.Create,
                    callback: async (cell: CellModel<Group>, _) => await this.saveTypeAsync(cell)
                },
                {
                    name: "cancel",
                    title: Localizer.genericActionCancelLanguageItemName,
                    icon: "far ban",
                    type: ActionType.Delete,
                    callback: async (cell) => await cell.row.cancelAsync()
                },
                {
                    name: "delete",
                    title: Localizer.genericActionDeleteLanguageItemName,
                    icon: "far trash-alt",
                    type: ActionType.Delete,
                    callback: async (cell) => await cell.grid.deleteAsync(cell.row.index)
                },
                {
                    name: "deletePermanently",
                    title: Localizer.genericActionDeleteLanguageItemName,
                    icon: "far trash-alt",
                    type: ActionType.Delete,
                    callback: async (cell) => await this.deletePermanentlyAsync(cell),
                    confirm: {
                        title: Localizer.genericDelete
                    }
                },
            ]
        }
    ];

    private get grid(): GridModel<Group> {
        return this._gridRef.current!.model;
    }

    private get newRowAlreadyExists(): boolean {
        return (this.grid.rows.some(row => !row.deleted && !row.model.id));
    }

    private initRow(row: RowModel<Group>): void {
        const model: Group = row.model;

        const isNew: boolean = ((model.id === "") || (model.id === null));
        const isValid: boolean = this.isNameValid(model);

        row.className = (!isValid) ? "danger" : (isNew) ? "bg-processed" : "";
    }

    private isNameValid(model: Group): boolean {
        return (model.name.trim().length > 0);
    }

    private initActions(cell: CellModel<Group>) {
        const model: Group = cell.row.model;
        const isNew: boolean = !model.id;
        const modified: boolean = cell.row.modified;
        const isValid: boolean = this.isNameValid(model);

        const saveAction: CellAction<Group> = getCellAction(cell.actions, "save");
        saveAction.visible = ((modified) || (isNew)) && (isValid);

        const cancelAction: CellAction<Group> = getCellAction(cell.actions, "cancel");
        cancelAction.visible = (modified) && (!isNew);

        const deleteAction: CellAction<Group> = getCellAction(cell.actions, "delete");
        deleteAction.visible = (isNew);

        const deletePermanentlyAction: CellAction<Group> = getCellAction(cell.actions, "deletePermanently");
        deletePermanentlyAction.visible = (!isNew);
    }

    private async getGroupsAsync(): Promise<Group[]> {
        return await this.getAsync("api/group/getGroups");
    }

    private async onNameChangeAsync(cell: CellModel<Group>): Promise<void> {
        const isValid: boolean = this.isNameValid(cell.model);

        await cell.setValidAsync(isValid);
    }

    private async saveTypeAsync(cell: CellModel<Group>): Promise<void> {
        if (cell.model.id === "") {
            // Create
            const request: AddGroupRequest = {
                name: cell.model.name,
            };

            await this.postAsync("api/group/addGroup", request);
        } else {
            // Update
            const request: SaveGroupRequest = {
                id: cell.model.id,
                name: cell.model.name,
            };

            await this.postAsync("api/group/saveGroup", request);
        }

        await this.refreshDataAsync();
    }

    private async deletePermanentlyAsync(cell: CellModel<Group>): Promise<void> {
        await this.postAsync("api/group/deleteGroup?groupId=" + cell.model.id, null);
        await this.refreshDataAsync();
    }

    private async onAddClickAsync(): Promise<void> {
        if (!this.newRowAlreadyExists) {
            const newGroup: Group = new Group();

            // Add to grid
            const rows: RowModel<Group>[] = await this.grid.insertAsync(0, newGroup);
            // Set focus to new groups's name column
            await rows[0].get(nameof(Group.name)).editAsync();
        }
    }

    public getTitle(): string {
        return Localizer.userManagementLabelUserGroups;
    }

    public async componentDidMount(): Promise<void> {
        await this.refreshDataAsync();
    }

    private async refreshDataAsync(): Promise<void> {
        const groups: Group[] = await this.getGroupsAsync();
        await this.setState({ groups: groups });
    }


    public render(): React.ReactNode {
        return (
            <PageContainer className={styles.workOrderTypeManagement}>
                <PageHeader title={Localizer.userManagementLabelUserGroups} />

                <PageRow>
                    <div className="col">

                        <div className={styles.container}>

                            <ToolbarContainer className={styles.toolbar}>

                                <Inline justify={JustifyContent.End}>

                                    <Button id="addGroup"
                                        icon={{ name: "plus", size: IconSize.Large }}
                                        title={Localizer.genericActionAdd}
                                        type={ButtonType.Orange}
                                        onClick={async (_, __) => await this.onAddClickAsync()}
                                    />

                                </Inline>

                            </ToolbarContainer>

                            <Grid responsive
                                id="groupsGrid"
                                ref={this._gridRef}
                                className={styles.grid}
                                minWidth="auto"
                                hovering={GridHoveringType.EditableCell}
                                odd={GridOddType.None}
                                columns={this._columns}
                                data={this.state.groups}
                                initRow={(row: RowModel<Group>) => this.initRow(row)}
                                noDataText={Localizer.genericNoData}
                            />

                        </div>

                    </div>
                </PageRow>
            </PageContainer>
        );
    }
}